import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, UserCheck, Car, CheckCircle, Loader2, AlertTriangle, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import TripSummary from "@/components/TripSummary";
import RouteMap from "@/components/RouteMap";
import { useVehiclePricing } from "@/hooks/useVehiclePricing";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const includedServices = [
  { icon: Shield, label: "Free Cancellation" },
  { icon: UserCheck, label: "Meet & Greet" },
  { icon: Car, label: "Private Transfer" },
  { icon: CheckCircle, label: "Licensed Chauffeurs" },
];

interface SelectedVehicle {
  id: string;
  name: string;
  image_url: string | null;
  price: number;
  capacity: number;
}

const VehicleSelection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedVehicle, setSelectedVehicle] = useState<SelectedVehicle | null>(null);

  const state = location.state as {
    from: string;
    to: string;
    fromPlaceId?: string;
    toPlaceId?: string;
    routeType: string;
    passengers: number;
    promoCode: string;
  } | null;

  const from = state?.from || "Punta Cana Airport (PUJ)";
  const to = state?.to || "Hard Rock Hotel Punta Cana";
  const routeType = state?.routeType || "one-way";
  const passengers = state?.passengers || 2;

  const { serviceGroups, loading, distanceKm, durationMin, noServiceAvailable } = useVehiclePricing(
    from,
    to,
    state?.fromPlaceId,
    state?.toPlaceId,
    routeType,
    passengers
  );

  const handleContinue = () => {
    if (!selectedVehicle) return;
    navigate("/booking", {
      state: {
        from,
        to,
        fromPlaceId: state?.fromPlaceId,
        toPlaceId: state?.toPlaceId,
        routeType,
        passengers,
        promoCode: state?.promoCode,
        distanceKm,
        durationMin,
        vehicle: selectedVehicle,
      },
    });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />

      <div className="container mx-auto px-4 py-8 pt-28">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground">
              Choose Your <span className="text-primary">Vehicle</span>
            </h1>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-4">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
                <p className="text-muted-foreground font-sans">Loading vehicles & prices...</p>
              </div>
            ) : noServiceAvailable ? (
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-center justify-center py-20 gap-6 text-center"
              >
                <AlertTriangle className="w-12 h-12 text-primary" />
                <div>
                  <h2 className="font-display text-xl font-bold text-foreground mb-2">
                    No Service Available
                  </h2>
                  <p className="text-muted-foreground font-sans max-w-md">
                    We currently don't have service available for this route. Please try a different combination.
                  </p>
                </div>
                <Button onClick={() => navigate("/")} className="font-sans font-bold">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to Search
                </Button>
              </motion.div>
            ) : serviceGroups.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-muted-foreground font-sans">No vehicles available for {passengers} passenger{passengers !== 1 ? "s" : ""} on this route.</p>
              </div>
            ) : (
              serviceGroups.map((group) => (
                <div key={group.id} className="space-y-4">
                  <h2 className="font-display text-xl font-bold text-foreground border-b border-border pb-2">
                    {group.name}
                  </h2>
                  {group.vehicles.map((vehicle, index) => {
                    const isSelected = selectedVehicle?.id === vehicle.id;
                    return (
                      <motion.div
                        key={vehicle.id}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        onClick={() =>
                          setSelectedVehicle({
                            id: vehicle.id,
                            name: vehicle.name,
                            image_url: vehicle.image_url,
                            price: vehicle.price,
                            capacity: vehicle.capacity,
                          })
                        }
                        className={`relative flex flex-col md:flex-row gap-6 p-6 rounded-2xl bg-card border-2 transition-all cursor-pointer group ${
                          isSelected
                            ? "border-primary shadow-lg ring-2 ring-primary/20"
                            : "border-border hover:border-primary/30 hover:shadow-lg"
                        }`}
                      >
                        {/* Selected indicator */}
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute top-3 right-3 w-7 h-7 rounded-full bg-primary flex items-center justify-center"
                          >
                            <Check className="w-4 h-4 text-primary-foreground" />
                          </motion.div>
                        )}

                        <div className="relative w-full md:w-56 h-40 rounded-xl overflow-hidden shrink-0">
                          <img
                            src={vehicle.image_url || "/placeholder.svg"}
                            alt={vehicle.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                          {vehicle.badge && (
                            <span className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-sans font-bold bg-primary/20 text-primary">
                              {vehicle.badge}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 flex flex-col justify-between">
                          <div>
                            <h3 className="font-display text-xl font-bold text-foreground">{vehicle.name}</h3>
                            <p className="text-muted-foreground font-sans text-sm mt-1">
                              {vehicle.min_passengers}–{vehicle.capacity} passengers
                            </p>
                            {(vehicle as any).description && (
                              <p className="text-muted-foreground font-sans text-sm mt-2 italic">{(vehicle as any).description}</p>
                            )}
                            {vehicle.features && vehicle.features.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-3">
                                {vehicle.features.map((f) => (
                                  <Badge key={f} variant="secondary" className="text-xs font-sans">
                                    {f}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex items-end justify-between mt-4">
                            <div>
                              <p className="text-3xl font-display font-bold text-primary">${vehicle.price}</p>
                              <p className="text-muted-foreground text-xs font-sans">
                                {routeType === "round-trip" ? "round trip" : "one way"}
                              </p>
                            </div>
                            <Button
                              variant={isSelected ? "default" : "outline"}
                              className="font-sans font-bold"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedVehicle({
                                  id: vehicle.id,
                                  name: vehicle.name,
                                  image_url: vehicle.image_url,
                                  price: vehicle.price,
                                  capacity: vehicle.capacity,
                                });
                              }}
                            >
                              {isSelected ? (
                                <>
                                  <Check className="w-4 h-4 mr-1" /> Selected
                                </>
                              ) : (
                                "Select"
                              )}
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-8 p-6 rounded-2xl bg-card border border-border">
              <h3 className="font-display text-lg font-bold text-foreground mb-6">
                Trip Summary
              </h3>

              <RouteMap from={from} to={to} />

              <TripSummary
                from={from}
                to={to}
                distanceKm={distanceKm}
                durationMin={durationMin}
                passengers={passengers}
                loadingRoute={loading}
                routeType={routeType}
              />

              <div className="border-t border-border pt-6">
                <p className="text-xs text-muted-foreground font-sans uppercase tracking-wide mb-3">
                  Included in all transfers
                </p>
                <div className="space-y-3">
                  {includedServices.map((s) => (
                    <div key={s.label} className="flex items-center gap-2">
                      <s.icon className="w-4 h-4 text-primary" />
                      <span className="text-foreground font-sans text-sm">{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom sticky bar */}
      <AnimatePresence>
        {selectedVehicle && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-card border-t-2 border-primary shadow-[0_-4px_20px_rgba(0,0,0,0.15)]"
          >
            <div className="container mx-auto px-4 py-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0">
                <div className="hidden sm:block w-14 h-14 rounded-xl overflow-hidden shrink-0 border border-border">
                  <img
                    src={selectedVehicle.image_url || "/placeholder.svg"}
                    alt={selectedVehicle.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="min-w-0">
                  <p className="font-display font-bold text-foreground truncate">{selectedVehicle.name}</p>
                  <p className="text-primary font-display font-bold text-lg">${selectedVehicle.price} <span className="text-muted-foreground text-xs font-sans font-normal">{routeType === "round-trip" ? "round trip" : "one way"}</span></p>
                </div>
              </div>
              <Button size="lg" className="font-sans font-bold shrink-0" onClick={handleContinue}>
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <Footer />
    </div>
  );
};

export default VehicleSelection;
